// src/components/AppSidebar.jsx — v2 (Syne/Outfit + tokens T + selo Meta)
//
// Refator visual jun/26 aplicando o design refinado:
//   - Fontes Syne (labels/UI) + Outfit (números/badges) — matching geral
//   - Tokens de cor T (consolidados, fácil de tunar num lugar só)
//   - Helpers: SectionLabel, NavButton, CascadeButton, SidebarDivider
//   - Selo "Parceira Oficial Meta" abaixo do logo
//   - Plan config por plano (starter/business/enterprise)
//   - Width 260 → 256, collapsed 76 → 72 (ligeira economia visual)
//
// PRESERVADO da versão anterior (zero risco):
//   - Automação com badge "Novo" + comingSoon=true (alert "Em breve")
//   - Meta cascade com rotas hash (#templates / #empresa) — AdminMeta
//     usa scrollIntoView no useEffect de hash, não muda
//   - Cascades auto-abertos quando URL atual bate com sub-rota
//   - localStorage do collapse + breakpoint auto-collapse
//   - CSS var --sidebar-width publicada pra outras páginas usarem
//   - Tooltip wrapping em modo colapsado
//   - Atalho no modo colapsado: Administrativo → /admin/chips,
//     Meta → /admin/meta direto
import { useState, useEffect } from 'react';
import {
  Box, Typography, List, ListItemButton, ListItemText,
  Avatar, Button, Chip, Collapse, Tooltip, IconButton,
  useTheme, useMediaQuery, Drawer,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiGrid, FiMessageSquare, FiCalendar, FiUsers, FiLogOut, FiSettings,
  FiChevronDown, FiChevronRight, FiChevronLeft, FiCpu,
  FiArrowUpCircle, FiCreditCard, FiBriefcase, FiZap, FiBook, FiUser, FiKey, FiClipboard, FiLock,
} from 'react-icons/fi';
import Logo from './Logo';
import api from '../services/Api';
import useAssinatura from '../hooks/useAssinatura';

// Período de teste (out/26): Agenda, Automação e Formulários abrem desfocados
// com o cartão de ativar (components/assinatura/BloqueioTeste). O cadeado no
// lugar do "Novo" avisa antes do clique.
const CADEADO_TESTE = (
  <Box component="span" role="img" aria-label="Abre com a assinatura ativa" title="Abre com a assinatura ativa"
    sx={{ display: 'inline-flex', alignItems: 'center', py: '2px' }}>
    <FiLock size={10} aria-hidden />
  </Box>
);

// ─── CONSTANTS ──────────────────────────────────────────────────────────────
const SIDEBAR_W           = 256;
const SIDEBAR_W_COLLAPSED = 72;
const LS_KEY              = 'cetto.sidebar.collapsed';

// Flag em memória (não persistida) — só reinicia em reload de página inteira.
// Evita hidratar o localStorage a cada mount de página (a sidebar remonta ao
// mudar de rota, mas o load do módulo permanece).
let _userHydrated = false;

// ─── TOKENS ─────────────────────────────────────────────────────────────────
// Fonte única de verdade pra cores e fontes da sidebar. Bate com a paleta v2
// usada em Dashboard, Register, Conversas, etc.
const T = {
  bg:          '#0A0F1A',
  bg2:         '#0E1525',
  border:      '#1A2540',
  green:       '#00D67C',
  greenDim:    'rgba(0,214,124,0.10)',
  greenBorder: 'rgba(0,214,124,0.28)',
  text:        '#DDE4F0',
  text2:       '#7A8799',
  text3:       '#3E4D60',
  fontHead:    '"Syne", "Segoe UI", sans-serif',
  fontNum:     '"Outfit", "Segoe UI", sans-serif',
};

// ─── SCROLLBAR ──────────────────────────────────────────────────────────────
const scrollbarSx = {
  '&::-webkit-scrollbar':       { width: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: T.border, borderRadius: 8 },
  '&::-webkit-scrollbar-thumb:hover': { background: T.text3 },
  scrollbarColor: `${T.border} transparent`,
  scrollbarWidth: 'thin',
};

// ─── PLAN COLORS ────────────────────────────────────────────────────────────
const planConfig = {
  business:   { bg: 'rgba(0,214,124,0.15)',   color: T.green,   border: T.greenBorder,           label: 'BUSINESS'   },
  enterprise: { bg: 'rgba(99,102,241,0.15)',  color: '#A5B4FC', border: 'rgba(99,102,241,0.3)',  label: 'ENTERPRISE' },
  starter:    { bg: 'rgba(91,164,245,0.12)',  color: '#93C5FD', border: 'rgba(91,164,245,0.25)', label: 'STARTER'    },
  // Plano sob medida (out/26, fora da landing): mesmo tom do Starter, que é a base dele.
  plus:       { bg: 'rgba(91,164,245,0.12)',  color: '#93C5FD', border: 'rgba(91,164,245,0.25)', label: 'PLUS'       },
};

// ─── ROLE LABELS ────────────────────────────────────────────────────────────
const roleLabel = { ADMIN: 'Admin', SUPERVISOR: 'Supervisor', AGENT: 'Agente' };

// ─── SECTION LABEL ──────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <Typography sx={{
      fontFamily: T.fontHead,
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: '0.18em',
      color: T.text3,
      textTransform: 'uppercase',
      px: '10px',
      mb: '6px',
      mt: '4px',
      display: 'block',
    }}>
      {children}
    </Typography>
  );
}

// ─── DIVIDER ────────────────────────────────────────────────────────────────
function SidebarDivider() {
  return <Box sx={{ height: '1px', bgcolor: T.border, mx: '10px', my: '14px' }} />;
}

// ─── NAV BUTTON ─────────────────────────────────────────────────────────────
// Suporta:
//   - active: pinta verde + borda
//   - size: 'md' | 'sm' (sub-itens dos cascades usam 'sm')
//   - indent: shift à direita pra sub-itens (collapsed ignora)
//   - badge: chip pequeno à direita (ex: "Novo" no Automação)
//   - badgeColor: cor do badge (default verde)
function NavButton({ icon, label, active, onClick, indent = false, collapsed, size = 'md', badge, badgeColor }) {
  const isSmall = size === 'sm';
  const bColor = badgeColor || T.green;
  const bBg = badgeColor ? `${badgeColor}1f` : T.greenDim;
  const bBorder = badgeColor ? `${badgeColor}55` : T.greenBorder;

  const inner = (
    <ListItemButton
      onClick={onClick}
      sx={{
        borderRadius: '7px',
        mb: '2px',
        px: collapsed ? 0 : '10px',
        py: isSmall ? '7px' : '8px',
        minHeight: isSmall ? 36 : 42,
        color: active ? T.green : T.text2,
        bgcolor: active ? T.greenDim : 'transparent',
        border: active ? `1px solid ${T.greenBorder}` : '1px solid transparent',
        gap: '10px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        ml: (!collapsed && indent) ? '8px' : 0,
        transition: 'all 0.15s ease',
        fontFamily: T.fontHead,
        '&:hover': {
          bgcolor: T.greenDim,
          color: T.green,
          border: `1px solid ${T.greenBorder}`,
        },
      }}
    >
      <Box sx={{ opacity: active ? 1 : 0.7, display: 'flex', flexShrink: 0 }}>
        {icon}
      </Box>
      {!collapsed && (
        <>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontFamily: T.fontHead,
              fontWeight: active ? 700 : 500,
              fontSize: isSmall ? '0.8rem' : '0.85rem',
              letterSpacing: '0.01em',
            }}
          />
          {badge && (
            <Box sx={{
              ml: 'auto',
              fontFamily: T.fontHead,
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              px: '7px', py: '1px',
              borderRadius: '6px',
              bgcolor: bBg,
              color: bColor,
              border: `1px solid ${bBorder}`,
              flexShrink: 0,
            }}>
              {badge}
            </Box>
          )}
        </>
      )}
    </ListItemButton>
  );

  return collapsed
    ? <Tooltip title={label} placement="right" arrow>{inner}</Tooltip>
    : inner;
}

// ─── CASCADE BUTTON ─────────────────────────────────────────────────────────
function CascadeButton({ icon, label, open, onClick, collapsed, active }) {
  const inner = (
    <ListItemButton
      onClick={onClick}
      sx={{
        borderRadius: '7px',
        mb: '2px',
        px: collapsed ? 0 : '10px',
        py: '8px',
        minHeight: 42,
        color: (open || active) ? T.green : T.text2,
        bgcolor: (open || active) ? T.greenDim : 'transparent',
        border: (open || active) ? `1px solid ${T.greenBorder}` : '1px solid transparent',
        gap: '10px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        transition: 'all 0.15s ease',
        '&:hover': { bgcolor: T.greenDim, color: T.green, border: `1px solid ${T.greenBorder}` },
      }}
    >
      <Box sx={{ opacity: (open || active) ? 1 : 0.7, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      {!collapsed && (
        <>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontFamily: T.fontHead,
              fontWeight: (open || active) ? 700 : 500,
              fontSize: '0.85rem',
              letterSpacing: '0.01em',
            }}
          />
          <Box sx={{ opacity: 0.5, ml: 'auto', display: 'flex' }}>
            {open ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
          </Box>
        </>
      )}
    </ListItemButton>
  );

  return collapsed
    ? <Tooltip title={label} placement="right" arrow>{inner}</Tooltip>
    : inner;
}

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
export default function AppSidebar({ user: userProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { emTeste } = useAssinatura();
  const selo = (padrao) => (emTeste ? CADEADO_TESTE : padrao);
  const corDoSelo = emTeste ? T.text2 : undefined;
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  // < md (900px) = comportamento mobile: sidebar vira drawer overlay disparado
  // pelo botão hambúrguer da TopBar via evento "sidebar-toggle".
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const open = () => setMobileOpen(true);
    const toggle = () => setMobileOpen(v => !v);
    window.addEventListener('sidebar-open', open);
    window.addEventListener('sidebar-toggle', toggle);
    return () => {
      window.removeEventListener('sidebar-open', open);
      window.removeEventListener('sidebar-toggle', toggle);
    };
  }, []);
  // Fecha o drawer ao navegar (padrão de app mobile: clicar em item leva
  // pra rota e o drawer some pra dar espaço ao conteúdo).
  useEffect(() => { if (isMobile) setMobileOpen(false); }, [location.pathname, isMobile]);

  // Espelha o user recebido via prop, mas reagir a "user-updated" (disparado
  // pela página de config quando o usuário edita perfil ou troca a foto).
  // Assim o avatar/nome no sidebar refletem a mudança sem recarregar a página.
  const [user, setUser] = useState(userProp);
  useEffect(() => { setUser(userProp); }, [userProp]);
  useEffect(() => {
    const handler = () => {
      try { setUser(JSON.parse(localStorage.getItem('user') || '{}')); }
      catch { /* localStorage inválido — mantém o atual */ }
    };
    window.addEventListener('user-updated', handler);
    return () => window.removeEventListener('user-updated', handler);
  }, []);

  // Hidrata o localStorage com o resultado de /users/me na primeira montagem
  // de cada page-load. Motivo: a resposta do /auth/login pode ficar defasada
  // em relação ao estado real do banco (ex: usuário que fez upload de foto
  // numa sessão antiga, deslogou e voltou; ou build antigo em cache). /users/me
  // é a fonte de verdade — depois do fetch, disparamos "user-updated" para
  // essa própria sidebar re-renderizar via localStorage.
  useEffect(() => {
    if (_userHydrated) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    _userHydrated = true;
    api.get('/users/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        try {
          const current = JSON.parse(localStorage.getItem('user') || '{}');
          const merged = {
            ...current,
            name: res.data.name,
            email: res.data.email,
            role: res.data.role,
            profile_photo_url: res.data.profile_photo_url,
            company_id: res.data.company_id ?? current.company_id,
          };
          localStorage.setItem('user', JSON.stringify(merged));
          window.dispatchEvent(new Event('user-updated'));
        } catch {
          /* localStorage inválido — próximo login corrige */
        }
      })
      .catch(() => { _userHydrated = false; }); // se falhou (401, offline), permite retry
  }, []);

  // ── Collapse state ──
  // Persiste no localStorage só o estado do desktop. Em mobile forçamos
  // `collapsed=false` para o drawer mostrar labels completas — não faz
  // sentido um drawer overlay em modo icon-only.
  const [collapsedRaw, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(LS_KEY);
    return stored !== null ? stored === 'true' : false;
  });
  const collapsed = isMobile ? false : collapsedRaw;
  const [userInteracted, setUserInteracted] = useState(
    () => localStorage.getItem(LS_KEY) !== null
  );

  useEffect(() => {
    if (isMobile) return; // auto-collapse só afeta desktop
    if (!userInteracted) setCollapsed(!isLgUp);
  }, [isLgUp, userInteracted, isMobile]);

  const width = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W;
  useEffect(() => {
    // Em mobile a sidebar vira drawer overlay — não ocupa espaço próprio no
    // layout, então o CSS var vira 0 para as páginas colarem no canto esquerdo.
    document.documentElement.style.setProperty(
      '--sidebar-width',
      isMobile ? '0px' : `${width}px`,
    );
  }, [width, isMobile]);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(LS_KEY, String(next));
      return next;
    });
    setUserInteracted(true);
  };

  // ── Cascades (preservado da versão anterior) ──
  // Cada cascade abre/fecha independente; auto-abre quando a URL atual
  // bate com uma das suas sub-rotas.
  const [adminOpen, setAdminOpen] = useState(false);

  useEffect(() => {
    if (collapsed) {
      setAdminOpen(false);
    }
  }, [collapsed]);

  useEffect(() => {
    if (collapsed) return;
    if (location.pathname.startsWith('/admin/') && !location.pathname.startsWith('/admin/meta')) {
      setAdminOpen(true);
    }
  }, [location.pathname, collapsed]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  // ─── MENU STRUCTURE ────────────────────────────────────────────────────
  // NAVEGAÇÃO: itens do dia-a-dia (todos os usuários operacionais).
  // GERENCIAMENTO (renderizado abaixo, fora do array): Automação,
  // Administrativo e Meta — só SUPERVISOR + ADMIN.
  //
  // Contatos (CRM) e Automação mantêm comingSoon=true enquanto a
  // página não existe: o clique dispara alert em vez de navegar pra
  // catch-all do App.js que redireciona pra "/".
  const navItems = [
    { label: 'Dashboard', icon: <FiGrid size={17} />,          path: '/dashboard', roles: ['SUPERVISOR', 'AGENT', 'ADMIN'] },
    { label: 'Conversas', icon: <FiMessageSquare size={17} />, path: '/conversas', roles: ['SUPERVISOR', 'AGENT', 'ADMIN'] },
    // Agenda nativa (out/26): todo mundo vê — o agente, só o que é dele.
    { label: 'Agenda',    icon: <FiCalendar size={17} />,      path: '/agenda',    roles: ['SUPERVISOR', 'AGENT', 'ADMIN'], badge: 'Novo' },
    { label: 'Agentes',   icon: <FiUsers size={17} />,         path: '/agents',    roles: ['SUPERVISOR'] },
    { label: 'Contatos',  icon: <FiBook size={17} />,          path: '/contatos',  roles: ['SUPERVISOR', 'AGENT'], badge: 'Novo' },
  ];

  const adminSubItems = [
    { label: 'Chips',            icon: <FiCpu size={14} />,           path: '/admin/chips' },
    { label: 'Upgrade de Plano', icon: <FiArrowUpCircle size={14} />, path: '/admin/upgrade' },
    { label: 'Assinatura',       icon: <FiCreditCard size={14} />,    path: '/admin/assinatura' },
    { label: 'Integrações',      icon: <FiKey size={14} />,           path: '/admin/integracoes' },
  ];

  // Meta vira um NavButton simples (sem cascade) — a página /admin/meta
  // agora tem abas internas (Dados / Templates), então duplicar a navegação
  // no sidebar virou ruído. Voltar a esse padrão antigo exigiria reintroduzir
  // navegação por hash (#templates / #empresa) e o scrollIntoView no
  // AdminMeta.js.

  const plan = (user?.plan || 'starter').toLowerCase();
  const pConf = planConfig[plan] || planConfig.starter;

  // ─── RENDER ────────────────────────────────────────────────────────────
  // Todo o markup interno é o mesmo — só o WRAPPER muda:
  //   Desktop → <Box position:fixed> ocupa a coluna esquerda do layout.
  //   Mobile  → <Drawer temporary> abre por cima, controlado pelo botão
  //             hambúrguer na TopBar.
  const inner = (
    <Box sx={{
      width: isMobile ? SIDEBAR_W : width,
      bgcolor: T.bg,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRight: isMobile ? 'none' : `1px solid ${T.border}`,
      transition: isMobile ? 'none' : 'width 0.22s ease',
    }}>

      {/* ═══ LOGO + SELO META + TOGGLE ═══ */}
      <Box sx={{
        px: collapsed ? '12px' : '18px',
        py: '18px',
        borderBottom: `1px solid ${T.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        gap: '8px',
        minHeight: 72,
      }}>
        {!collapsed && (
          <Box>
            <Logo light={true} />
            {/* Selo Parceira Oficial Meta — abaixo do logo, ciano sutil */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              mt: '7px',
              px: '8px', py: '2px',
              borderRadius: '20px',
              background: 'rgba(0,212,232,0.08)',
              border: '1px solid rgba(56,189,248,0.18)',
            }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#38BDF8' }} />
              <Typography sx={{
                fontFamily: T.fontHead,
                fontSize: '0.58rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: '#38BDF8',
                textTransform: 'uppercase',
              }}>
                Parceira Oficial Meta
              </Typography>
            </Box>
          </Box>
        )}

        <Tooltip title={collapsed ? 'Expandir menu' : 'Recolher menu'} placement="right" arrow>
          <IconButton
            onClick={toggleCollapsed}
            size="small"
            sx={{
              color: T.text3,
              width: 30, height: 30,
              bgcolor: 'rgba(255,255,255,0.03)',
              border: `1px solid ${T.border}`,
              borderRadius: '7px',
              flexShrink: 0,
              '&:hover': { bgcolor: T.greenDim, color: T.green, borderColor: T.greenBorder },
              transition: 'all 0.15s ease',
            }}
          >
            {collapsed ? <FiChevronRight size={14} /> : <FiChevronLeft size={14} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ═══ MENU PRINCIPAL ═══ */}
      <Box sx={{
        flexGrow: 1,
        px: collapsed ? '10px' : '12px',
        pt: '16px',
        overflowY: 'auto',
        ...scrollbarSx,
      }}>

        {/* NAVEGAÇÃO */}
        {!collapsed && <SectionLabel>Navegação</SectionLabel>}
        <List disablePadding>
          {navItems
            .filter(item => item.roles.includes(user?.role))
            .map(item => (
              <NavButton
                key={item.path}
                icon={item.icon}
                label={item.label}
                badge={item.path === '/agenda' ? selo(item.badge) : item.badge}
                badgeColor={item.path === '/agenda' ? corDoSelo : undefined}
                active={location.pathname === item.path}
                onClick={() => {
                  // comingSoon: rota não existe ainda — mostra alert em vez
                  // de navegar pra catch-all do App.js (que redireciona pra /).
                  if (item.comingSoon) {
                    alert(`"${item.label}" — disponível em breve.`);
                    return;
                  }
                  navigate(item.path);
                }}
                collapsed={collapsed}
              />
            ))}
        </List>

        {/* GERENCIAMENTO */}
        {['SUPERVISOR', 'ADMIN'].includes(user?.role) && (
          <Box>
            <SidebarDivider />
            {!collapsed && <SectionLabel>Gerenciamento</SectionLabel>}

            {/* ── AUTOMAÇÃO — página ativa (welcome first contact) ── */}
            <NavButton
              icon={<FiZap size={17} />}
              label="Automação"
              badge={selo('Novo')}
              badgeColor={corDoSelo}
              active={location.pathname === '/automacao'}
              onClick={() => navigate('/automacao')}
              collapsed={collapsed}
            />

            {/* ── FORMULÁRIOS — WhatsApp Flows (out/26) ── */}
            <NavButton
              icon={<FiClipboard size={17} />}
              label="Formulários"
              badge={selo('Novo')}
              badgeColor={corDoSelo}
              active={location.pathname === '/formularios'}
              onClick={() => navigate('/formularios')}
              collapsed={collapsed}
            />

            {/* ── ADMINISTRATIVO ── */}
            <CascadeButton
              icon={<FiSettings size={17} />}
              label="Administrativo"
              open={adminOpen}
              active={adminSubItems.some(s => location.pathname === s.path)}
              onClick={collapsed
                ? () => navigate('/admin/chips')
                : () => setAdminOpen(p => !p)
              }
              collapsed={collapsed}
            />

            {!collapsed && (
              <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ pl: '8px', mt: '2px' }}>
                  {adminSubItems.map(item => (
                    <NavButton
                      key={item.path}
                      icon={item.icon}
                      label={item.label}
                      active={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                      collapsed={collapsed}
                      size="sm"
                    />
                  ))}
                </List>
              </Collapse>
            )}

            {/* ── META ── */}
            <Box sx={{ mt: '2px' }}>
              <NavButton
                icon={<FiBriefcase size={17} />}
                label="Meta"
                active={location.pathname === '/admin/meta'}
                onClick={() => navigate('/admin/meta')}
                collapsed={collapsed}
              />
            </Box>
          </Box>
        )}

        {/* CONTA — visível pra todos os usuários autenticados */}
        <Box>
          <SidebarDivider />
          {!collapsed && <SectionLabel>Conta</SectionLabel>}
          <NavButton
            icon={<FiUser size={17} />}
            label="Configurações da conta"
            active={location.pathname === '/config-conta'}
            onClick={() => navigate('/config-conta')}
            collapsed={collapsed}
          />
        </Box>
      </Box>

      {/* ═══ RODAPÉ ═══ */}
      <Box sx={{
        p: collapsed ? '12px' : '16px',
        borderTop: `1px solid ${T.border}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: collapsed ? 'center' : 'stretch',
      }}>

        {/* PLANO */}
        {!collapsed && (
          <Chip
            label={pConf.label}
            size="small"
            sx={{
              fontFamily: T.fontHead,
              bgcolor: pConf.bg,
              color: pConf.color,
              border: `1px solid ${pConf.border}`,
              fontWeight: 700,
              fontSize: '0.62rem',
              letterSpacing: '0.12em',
              height: 22,
              borderRadius: '6px',
              alignSelf: 'flex-start',
            }}
          />
        )}

        {/* USUÁRIO */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          <Tooltip
            title={collapsed ? `${user?.name || 'Usuário'} · ${roleLabel[user?.role] || 'Usuário'}` : ''}
            placement="right"
            arrow
          >
            <Avatar
              src={
                user?.profile_photo_url
                  ? (user.profile_photo_url.startsWith('http')
                      ? user.profile_photo_url
                      : `${process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br'}${user.profile_photo_url}`)
                  : undefined
              }
              sx={{
                bgcolor: T.greenDim,
                border: `1.5px solid ${T.greenBorder}`,
                width: 34, height: 34,
                fontSize: '0.85rem',
                fontFamily: T.fontHead,
                fontWeight: 700,
                color: T.green,
                flexShrink: 0,
                cursor: 'pointer',
              }}
              onClick={() => navigate('/config-conta')}
            >
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </Tooltip>

          {!collapsed && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontFamily: T.fontHead,
                fontSize: '0.82rem',
                fontWeight: 700,
                color: T.text,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {user?.name || 'Usuário'}
              </Typography>
              <Typography sx={{
                fontFamily: T.fontHead,
                fontSize: '0.7rem',
                color: T.text3,
                mt: '1px',
              }}>
                {roleLabel[user?.role] || 'Usuário'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* SAIR */}
        {collapsed ? (
          <Tooltip title="Sair da conta" placement="right" arrow>
            <IconButton
              onClick={handleLogout}
              size="small"
              sx={{
                color: T.text3,
                width: 32, height: 32,
                border: `1px solid ${T.border}`,
                borderRadius: '7px',
                '&:hover': { bgcolor: 'rgba(220,38,38,0.10)', color: '#F87171', borderColor: 'rgba(220,38,38,0.25)' },
                transition: 'all 0.15s ease',
              }}
            >
              <FiLogOut size={15} />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            fullWidth
            startIcon={<FiLogOut size={14} />}
            onClick={handleLogout}
            sx={{
              fontFamily: T.fontHead,
              color: T.text3,
              textTransform: 'none',
              justifyContent: 'flex-start',
              fontWeight: 500,
              fontSize: '0.82rem',
              py: '7px',
              px: '10px',
              borderRadius: '7px',
              border: '1px solid transparent',
              '&:hover': {
                bgcolor: 'rgba(220,38,38,0.08)',
                color: '#F87171',
                border: '1px solid rgba(220,38,38,0.2)',
              },
              transition: 'all 0.15s ease',
            }}
          >
            Sair da conta
          </Button>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            bgcolor: T.bg,
            border: 'none',
            width: SIDEBAR_W,
            boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
          },
        }}
      >
        {inner}
      </Drawer>
    );
  }

  return (
    <Box sx={{
      position: 'fixed',
      left: 0, top: 0,
      height: '100vh',
      zIndex: 1200,
    }}>
      {inner}
    </Box>
  );
}
